import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalendarCheck, Loader2, MessageCircle, Phone, Send, User, X } from "lucide-react";
import { site } from "@/config/site";
import { useLeadForm } from "@/components/lead/LeadForm";
import { trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.11 3.22 5.1 4.51.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.83 9.83 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.82 9.82 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88m8.42-18.29A11.8 11.8 0 0 0 12.04 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.9 11.9 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.48-8.4" />
  </svg>
);

const WELCOME = {
  role: "assistant",
  text: "Hi! I'm the My Blinds Company assistant. Ask me anything about our blinds, shutters or fitting — or tap an option below and I'll sort it.",
};

const CHIPS = [
  { label: "How much do blinds cost?", send: "How much do blinds cost roughly?" },
  { label: "Which areas do you cover?", send: "Which areas do you cover?" },
  { label: "Book a free visit", action: "book" },
  { label: "Talk to a person", action: "person" },
];

const getSid = () => {
  let sid = localStorage.getItem("mbc_chat_sid");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : `s${Date.now()}${Math.random().toString(36).slice(2, 10)}`).replace(/[^A-Za-z0-9_-]/g, "-");
    localStorage.setItem("mbc_chat_sid", sid);
  }
  return sid;
};

export function ChatWidget() {
  const { openForm } = useLeadForm();
  const location = useLocation();
  const sid = useMemo(getSid, []);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat"); // "chat" | "person" | "done"
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [cb, setCb] = useState({ name: "", phone: "", message: "", consent: false });
  const [cbError, setCbError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, mode]);

  const openChat = () => {
    setOpen(true);
    setMode("chat");
    trackEvent("chat_open", { page: location.pathname });
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((ms) => [...ms, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text, page: location.pathname }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Chat is unavailable right now");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const evt of events) {
          const line = evt.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let data;
          try { data = JSON.parse(line.slice(6)); } catch { continue; }
          if (data.error) throw new Error(data.error);
          if (data.token) {
            setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, text: m.text + data.token } : m)));
          }
          if (data.done) {
            if (typeof data.final === "string") {
              setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, text: data.final } : m)));
            }
            if (data.lead_captured) {
              setLeadCaptured(true);
              trackEvent("chat_lead_captured", { page: location.pathname });
            }
          }
        }
      }
    } catch (e) {
      setMessages((ms) => ms.map((m, i) => (i === ms.length - 1
        ? { ...m, text: `${e.message || "Something went wrong"} — you can also call us on ${site.phone.display}.` }
        : m)));
    } finally {
      setBusy(false);
    }
  };

  const submitCallback = async () => {
    setCbError("");
    if (!cb.name.trim() || !cb.phone.trim()) { setCbError("Please add your name and phone number."); return; }
    if (!cb.consent) { setCbError("Please tick the box so we're allowed to call you back."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/callback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cb.name, phone: cb.phone, message: cb.message, page: location.pathname, consentService: cb.consent }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not send your request");
      setMode("done");
      setLeadCaptured(true);
      trackEvent("chat_callback_request", { page: location.pathname });
    } catch (e) {
      setCbError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const chipClick = (chip) => {
    if (chip.action === "book") { openForm({ source: "chat_widget" }); return; }
    if (chip.action === "person") { setMode("person"); return; }
    send(chip.send);
  };

  return (
    <div className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-40 flex flex-col items-end gap-3" data-testid="chat-widget">
      {open && (
        <div
          className="w-[min(92vw,380px)] h-[min(560px,68vh)] rounded-2xl bg-white shadow-2xl border border-warm-200 flex flex-col overflow-hidden"
          role="dialog"
          aria-label="Chat with My Blinds Company"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-navy-950 px-4 py-3.5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
              <MessageCircle className="h-4.5 w-4.5 text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white leading-tight">Chat with us</p>
              <p className="text-[11px] text-white/50 leading-tight flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" /> Instant answers, 24/7
              </p>
            </div>
            <a
              href={`${site.whatsapp.href}?text=${encodeURIComponent("Hi My Blinds Company — I'd like to chat about blinds for my home.")}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Continue on WhatsApp"
              data-testid="chat-header-whatsapp"
              className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <WhatsAppIcon className="h-4 w-4" />
            </a>
            <button type="button" aria-label="Close chat" data-testid="chat-close" onClick={() => setOpen(false)} className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {mode === "chat" && (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-warm-50/60" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-navy-900 text-white rounded-br-md"
                        : "bg-white border border-warm-200 text-slate-700 rounded-bl-md shadow-sm"
                    }`} data-testid={`chat-msg-${i}`}>
                      {m.text || (busy && i === messages.length - 1 ? (
                        <span className="inline-flex gap-1 py-1" aria-label="Typing">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce" />
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:120ms]" />
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:240ms]" />
                        </span>
                      ) : m.text)}
                    </div>
                  </div>
                ))}
                {leadCaptured && (
                  <p className="text-center text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mx-auto w-fit" data-testid="chat-lead-captured">
                    Details received — a salesperson will ring you back shortly
                  </p>
                )}
              </div>

              {/* Quick actions */}
              <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 border-t border-warm-200 bg-white">
                {CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    data-testid={`chat-chip-${chip.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => chipClick(chip)}
                    disabled={busy}
                    className="rounded-full border border-warm-300 px-3 py-1.5 text-[11px] font-semibold text-navy-900 hover:border-navy-800/50 hover:bg-warm-100 transition-colors disabled:opacity-40"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Input */}
              <form
                className="flex items-center gap-2 p-3 bg-white"
                onSubmit={(e) => { e.preventDefault(); send(); }}
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question…"
                  aria-label="Type your message"
                  data-testid="chat-input"
                  maxLength={1000}
                  className="flex-1 rounded-full border border-warm-300 px-4 py-2.5 text-sm focus:border-navy-900 focus:outline-none"
                />
                <button
                  type="submit"
                  aria-label="Send message"
                  data-testid="chat-send"
                  disabled={busy || !input.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy-900 text-white hover:bg-navy-800 transition-colors disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}

          {mode === "person" && (
            <div className="flex-1 overflow-y-auto p-5 space-y-3.5 bg-warm-50/60" data-testid="chat-person-form">
              <div>
                <h3 className="font-serif text-lg text-navy-900">Talk to a salesperson</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Leave your details and the team will ring you back — usually the same working day. Prefer to talk now? Call {site.phone.display}.
                </p>
              </div>
              <div>
                <label htmlFor="cb-name" className="text-xs font-semibold text-slate-500 block mb-1">Your name</label>
                <input id="cb-name" data-testid="callback-name" value={cb.name} onChange={(e) => setCb({ ...cb, name: e.target.value })} className="w-full rounded-xl border border-warm-300 px-3.5 py-2.5 text-sm focus:border-navy-900 focus:outline-none" placeholder="e.g. Amjid" />
              </div>
              <div>
                <label htmlFor="cb-phone" className="text-xs font-semibold text-slate-500 block mb-1">Best phone number</label>
                <input id="cb-phone" type="tel" data-testid="callback-phone" value={cb.phone} onChange={(e) => setCb({ ...cb, phone: e.target.value })} className="w-full rounded-xl border border-warm-300 px-3.5 py-2.5 text-sm focus:border-navy-900 focus:outline-none" placeholder="07…" />
              </div>
              <div>
                <label htmlFor="cb-msg" className="text-xs font-semibold text-slate-500 block mb-1">What's it about? <span className="font-normal text-slate-400">(optional)</span></label>
                <textarea id="cb-msg" rows={2} data-testid="callback-message" value={cb.message} onChange={(e) => setCb({ ...cb, message: e.target.value })} className="w-full rounded-xl border border-warm-300 px-3.5 py-2.5 text-sm focus:border-navy-900 focus:outline-none resize-none" placeholder="e.g. Roller blinds for 3 bedroom windows" />
              </div>
              <label className="flex items-start gap-2.5 text-[11px] text-slate-500 leading-snug cursor-pointer">
                <input type="checkbox" data-testid="callback-consent" checked={cb.consent} onChange={(e) => setCb({ ...cb, consent: e.target.checked })} className="mt-0.5 h-4 w-4 rounded border-warm-300 accent-navy-900" />
                I'm happy for My Blinds Company to contact me about my enquiry.
              </label>
              {cbError && <p role="alert" data-testid="callback-error" className="text-xs text-red-700">{cbError}</p>}
              <div className="flex gap-2 pt-1">
                <button type="button" data-testid="callback-back" onClick={() => setMode("chat")} className="rounded-full border border-warm-300 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:border-navy-800/40 transition-colors">
                  Back to chat
                </button>
                <button type="button" data-testid="callback-submit" onClick={submitCallback} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-navy-800 transition-colors disabled:opacity-40">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />} Request callback
                </button>
              </div>
            </div>
          )}

          {mode === "done" && (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-warm-50/60" data-testid="chat-callback-done">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-4">
                <User className="h-6 w-6 text-emerald-700" />
              </span>
              <h3 className="font-serif text-xl text-navy-900">Sorted, {cb.name.split(" ")[0]}!</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Your callback request is with the team — a salesperson will ring <strong>{cb.phone}</strong> shortly.
              </p>
              <p className="text-xs text-slate-400 mt-3">Need us sooner? Call {site.phone.display} (Mon–Fri, 9–5).</p>
              <button type="button" data-testid="callback-done-back" onClick={() => setMode("chat")} className="mt-5 rounded-full border border-warm-300 px-5 py-2.5 text-xs font-semibold text-slate-600 hover:border-navy-800/40 transition-colors">
                Back to chat
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating buttons */}
      <a
        href={`${site.whatsapp.href}?text=${encodeURIComponent("Hi My Blinds Company — I'd like to chat about blinds for my home.")}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat now on WhatsApp"
        data-testid="whatsapp-fab"
        onClick={() => trackEvent("whatsapp_click", { page: location.pathname })}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition-transform duration-300 hover:scale-110 hover:-translate-y-0.5"
      >
        <WhatsAppIcon className="h-6 w-6" />
      </a>
      <button
        type="button"
        data-testid="chat-launcher"
        onClick={() => (open ? setOpen(false) : openChat())}
        aria-label={open ? "Close chat" : "Chat with us"}
        className="group flex items-center gap-2.5 rounded-full bg-navy-900 pl-4 pr-5 py-3 text-white shadow-2xl transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="text-sm font-semibold">{open ? "Close" : "Chat now"}</span>
      </button>
      {!open && (
        <button
          type="button"
          data-testid="chat-book-visit-fab"
          onClick={() => openForm({ source: "floating_dock" })}
          className="flex items-center gap-2 rounded-full border border-navy-900/15 bg-white/95 backdrop-blur px-4 py-2 text-[11px] font-semibold text-navy-900 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
        >
          <CalendarCheck className="h-3.5 w-3.5" /> Free design visit
        </button>
      )}
    </div>
  );
}
